<script setup>
// CRIAR / EDITAR RAMAL (telefonia, fase 1, 25/09/2026).
// O número é sugerido (o próximo livre) e não muda depois de criado — ele é o
// usuário SIP do aparelho. Quem usa, nome no visor, gravar e ligado/desligado
// podem mudar a qualquer hora e vão para a central na mesma hora.
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import AdvupSelect from 'dashboard/components-next/AdvupSelect.vue';
import { NUMERO_DE_RAMAL } from 'dashboard/helper/telefonia';
import { iniciaisDoNome, corDoNome } from 'dashboard/helper/avatar';

const props = defineProps({
  ramal: { type: Object, default: null },
  sugestao: { type: String, default: '' },
  usuarios: { type: Array, default: () => [] },
  numerosUsados: { type: Array, default: () => [] },
  salvando: { type: Boolean, default: false },
  erro: { type: String, default: '' },
});
const emit = defineEmits(['close', 'salvar']);

const { t } = useI18n();
const T = 'TELEFONIA.MODAL';
const editando = computed(() => Boolean(props.ramal));

const numero = ref(props.ramal?.numero || props.sugestao);
const nome = ref(props.ramal?.nome || '');
const usuarioId = ref(props.ramal?.usuario?.id ?? '');
const gravar = ref(props.ramal ? props.ramal.gravar : true);
const ativo = ref(props.ramal ? props.ramal.ativo : true);
// regras de saída (etapa D): para fora liga por padrão; exterior, não — golpe
// de ligação internacional é o maior risco de um PABX
const paraFora = ref(props.ramal ? props.ramal.liga_para_fora !== false : true);
const paraExterior = ref(Boolean(props.ramal?.liga_para_exterior));
const tentou = ref(false);

const opcoesDePessoa = computed(() => [
  { value: '', label: t(`${T}.PESSOA_NENHUMA`) },
  ...props.usuarios.map(u => ({ value: u.id, label: u.nome })),
]);
const pessoa = computed(() =>
  props.usuarios.find(u => u.id === usuarioId.value)
);

const erroDoNumero = computed(() => {
  if (editando.value) return '';
  if (!NUMERO_DE_RAMAL.test(numero.value.trim()))
    return t(`${T}.NUMERO_INVALIDO`);
  if (props.numerosUsados.includes(numero.value.trim()))
    return t(`${T}.NUMERO_USADO`);
  return '';
});

function salvar() {
  tentou.value = true;
  if (erroDoNumero.value) return;
  emit('salvar', {
    number: numero.value.trim(),
    name: nome.value.trim(),
    user_id: usuarioId.value || null,
    record_calls: gravar.value,
    enabled: ativo.value,
    can_call_external: paraFora.value,
    can_call_international: paraFora.value && paraExterior.value,
  });
}
</script>

<template>
  <div class="advup-modal-fundo" @click.self="emit('close')">
    <div
      v-modal-acessivel="() => emit('close')"
      class="advup-modal advup-modal--estreito tel-ramal"
    >
      <div class="advup-modal__topo advup-modal__topo--teal">
        <span class="advup-modal__selo" aria-hidden="true">
          <i class="i-ph-phone-plus-fill" />
        </span>
        <div class="min-w-0 flex-1">
          <h3 class="advup-modal__titulo">
            {{
              editando
                ? t(`${T}.EDITAR`, { numero: ramal.numero })
                : t(`${T}.NOVO`)
            }}
          </h3>
          <p class="advup-modal__sub">
            {{ editando ? t(`${T}.SUB_EDITAR`) : t(`${T}.SUB_NOVO`) }}
          </p>
        </div>
        <button
          type="button"
          class="advup-modal__fechar"
          :aria-label="t(`${T}.CANCELAR`)"
          @click="emit('close')"
        >
          <i class="i-lucide-x" />
        </button>
      </div>

      <form
        class="advup-modal__corpo tel-ramal__corpo"
        @submit.prevent="salvar"
      >
        <!-- O número em destaque: é o que a equipe decora e disca. -->
        <label
          class="tel-ramal__numero"
          :class="{ 'tel-ramal__numero--fixo': editando }"
        >
          <span class="advup-rotulo">{{ t(`${T}.NUMERO`) }}</span>
          <input
            v-model="numero"
            type="text"
            inputmode="numeric"
            maxlength="4"
            class="tel-ramal__digitos reset-base"
            :class="{ 'tel-ramal__digitos--erro': tentou && erroDoNumero }"
            :disabled="editando"
            :aria-invalid="Boolean(tentou && erroDoNumero)"
          />
          <span
            v-if="tentou && erroDoNumero"
            class="advup-nota advup-nota--erro"
          >
            {{ erroDoNumero }}
          </span>
          <span v-else-if="!editando" class="advup-nota">{{
            t(`${T}.NUMERO_DICA`)
          }}</span>
        </label>

        <div class="tel-ramal__campo">
          <span class="advup-rotulo">{{ t(`${T}.PESSOA`) }}</span>
          <div class="tel-ramal__pessoa">
            <span
              class="advup-avatar"
              :class="
                pessoa
                  ? `advup-avatar--${corDoNome(pessoa.nome)}`
                  : 'tel-ramal__sem-rosto'
              "
              aria-hidden="true"
            >
              <i v-if="!pessoa" class="i-ph-user" />
              <template v-else>{{ iniciaisDoNome(pessoa.nome) }}</template>
            </span>
            <AdvupSelect
              v-model="usuarioId"
              :options="opcoesDePessoa"
              :rotulo="t(`${T}.PESSOA`)"
              min-width="100%"
            />
          </div>
        </div>

        <label class="tel-ramal__campo">
          <span class="advup-rotulo">{{ t(`${T}.NOME`) }}</span>
          <input
            v-model="nome"
            type="text"
            maxlength="60"
            class="advup-campo reset-base"
            :placeholder="pessoa?.nome || t(`${T}.NOME_PLACEHOLDER`)"
          />
          <span class="advup-nota">{{ t(`${T}.NOME_DICA`) }}</span>
        </label>

        <button
          type="button"
          role="switch"
          class="advup-chave"
          :aria-checked="String(gravar)"
          @click="gravar = !gravar"
        >
          <span class="advup-chave__trilho" aria-hidden="true" />
          <span class="advup-chave__txt">
            <span class="advup-chave__nome">{{ t(`${T}.GRAVAR`) }}</span>
            <span class="advup-chave__sub">{{ t(`${T}.GRAVAR_DICA`) }}</span>
          </span>
        </button>

        <button
          type="button"
          role="switch"
          class="advup-chave"
          :aria-checked="String(paraFora)"
          @click="paraFora = !paraFora"
        >
          <span class="advup-chave__trilho" aria-hidden="true" />
          <span class="advup-chave__txt">
            <span class="advup-chave__nome">{{ t(`${T}.FORA`) }}</span>
            <span class="advup-chave__sub">{{ t(`${T}.FORA_DICA`) }}</span>
          </span>
        </button>

        <button
          v-if="paraFora"
          type="button"
          role="switch"
          class="advup-chave advup-chave--ambar"
          :aria-checked="String(paraExterior)"
          @click="paraExterior = !paraExterior"
        >
          <span class="advup-chave__trilho" aria-hidden="true" />
          <span class="advup-chave__txt">
            <span class="advup-chave__nome">{{ t(`${T}.EXTERIOR`) }}</span>
            <span class="advup-chave__sub">{{ t(`${T}.EXTERIOR_DICA`) }}</span>
          </span>
        </button>

        <button
          v-if="editando"
          type="button"
          role="switch"
          class="advup-chave advup-chave--ambar"
          :aria-checked="String(ativo)"
          @click="ativo = !ativo"
        >
          <span class="advup-chave__trilho" aria-hidden="true" />
          <span class="advup-chave__txt">
            <span class="advup-chave__nome">{{ t(`${T}.ATIVO`) }}</span>
            <span class="advup-chave__sub">{{ t(`${T}.ATIVO_DICA`) }}</span>
          </span>
        </button>

        <p v-if="erro" class="tel-ramal__erro" role="alert">
          <i class="i-ph-warning-octagon-fill" aria-hidden="true" />
          <span>{{ erro }}</span>
        </p>
      </form>

      <div class="advup-modal__rodape advup-modal__rodape--acoes">
        <button type="button" class="advup-btn--vazado" @click="emit('close')">
          {{ t(`${T}.CANCELAR`) }}
        </button>
        <button
          type="button"
          class="advup-btn-forte"
          :disabled="salvando"
          @click="salvar"
        >
          <i
            :class="
              salvando
                ? 'i-ph-circle-notch advup-gira'
                : editando
                  ? 'i-ph-check-bold'
                  : 'i-ph-phone-plus-fill'
            "
            aria-hidden="true"
          />
          {{
            salvando
              ? t(`${T}.SALVANDO`)
              : editando
                ? t(`${T}.SALVAR`)
                : t(`${T}.CRIAR`)
          }}
        </button>
      </div>
    </div>
  </div>
</template>

<style scoped>
.tel-ramal__corpo {
  display: flex;
  flex-direction: column;
  gap: var(--esp-4);
}

.tel-ramal__numero,
.tel-ramal__campo {
  display: flex;
  flex-direction: column;
  gap: var(--esp-2);
}

/* O número grande, em algarismos de mostrador: é o dado principal do ramal. */
.tel-ramal__digitos {
  width: 100%;
  padding: var(--esp-3) var(--esp-4);
  border: 1px solid rgb(var(--slate-5));
  border-radius: var(--r-4);
  background: radial-gradient(
      circle at 0 0,
      rgb(var(--teal-9) / 10%),
      transparent 60%
    ),
    rgb(var(--solid-1));
  font-family: var(--fonte-display);
  font-size: var(--txt-num-g);
  font-weight: var(--peso-num);
  line-height: var(--linha-num);
  letter-spacing: var(--rastro-rotulo);
  font-variant-numeric: tabular-nums;
  color: rgb(var(--slate-12));
  transition:
    border-color var(--mov-1) var(--mov-curva),
    box-shadow var(--mov-1) var(--mov-curva);
}

.tel-ramal__digitos:focus {
  outline: none;
  border-color: rgb(var(--teal-8));
  box-shadow: 0 0 0 3px rgb(var(--teal-9) / 18%);
}

.tel-ramal__digitos:disabled {
  color: rgb(var(--slate-11));
  cursor: not-allowed;
}

.tel-ramal__digitos--erro,
.tel-ramal__digitos--erro:focus {
  border-color: rgb(var(--ruby-8));
  box-shadow: 0 0 0 3px rgb(var(--ruby-9) / 14%);
}

.tel-ramal__pessoa {
  display: flex;
  align-items: center;
  gap: var(--esp-2);
}

.tel-ramal__pessoa > :last-child {
  flex: 1;
  min-width: 0;
}

.tel-ramal__sem-rosto {
  background: rgb(var(--slate-3));
  color: rgb(var(--slate-10));
}

.tel-ramal__erro {
  display: flex;
  align-items: flex-start;
  gap: var(--esp-2);
  margin: 0;
  padding: var(--esp-2) var(--esp-3);
  border-radius: var(--r-3);
  background: rgb(var(--ruby-3));
  font-size: var(--txt-nota);
  line-height: var(--linha-solta);
  color: rgb(var(--ruby-12));
}

.tel-ramal__erro > i {
  flex-shrink: 0;
  margin-top: 0.2em;
  color: rgb(var(--ruby-9));
}
</style>
